/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "magnite",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Magnite",
          "depth": 0,
          "id": "connect-to-magnite"
        }, {
          "value": "Magnite Data integration",
          "depth": 0,
          "id": "magnite-data-integration"
        }, {
          "value": "Sync configuration",
          "depth": 1,
          "id": "sync-configuration"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences"
        }, {
          "value": "Magnite Match integration",
          "depth": 0,
          "id": "magnite-match-integration"
        }, {
          "value": "Sync configuration",
          "depth": 1,
          "id": "sync-configuration-1"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences-1"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      code: "code",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Access Data Audiences"
              })
            }), _jsxs(_components.td, {
              children: ["Sync data from any source to Magnite Data audiences (", _jsx(_components.code, {
                children: "ctvid"
              }), ", ", _jsx(_components.code, {
                children: "ip"
              }), ", ", _jsx(_components.code, {
                children: "maid"
              }), ", ", _jsx(_components.code, {
                children: "cookie"
              }), ")"]
            }), _jsx(_components.td, {
              children: "All"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Match Audiences"
              })
            }), _jsxs(_components.td, {
              children: ["Sync data from any source to Magnite Match audiences (", _jsx(_components.code, {
                children: "hem"
              }), ")"]
            }), _jsx(_components.td, {
              children: "All"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-magnite",
        children: "Connect to Magnite"
      }), "\n", _jsx(_components.p, {
        children: "This integration requires prior approval from the Magnite team and the Hightouch team."
      }), "\n", _jsxs(_components.p, {
        children: ["To gain approval, please ", _jsx(PylonLink, {
          pylonText: "Hi, I need approval to connect Magnite.",
          linkText: "contact the Hightouch support team"
        }), " and connect us directly with your Magnite rep who will verify approval. Once we have confirmed, we will unlock the integration for you to connect."]
      }), "\n", _jsx(_components.p, {
        children: "To use the Magnite Data integration, enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client subfolder"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Owner"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To use the Magnite Match integration, enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Client name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Account ID"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Magnite destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin."]
      }), "\n", _jsx(_components.h2, {
        id: "magnite-data-integration",
        children: "Magnite Data integration"
      }), "\n", _jsx(_components.h3, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.p, {
        children: "Select the relevant model and the Magnite Data destination you want to sync to. Then, enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Segment name"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Price type"
          }), " (acceptable values are ", _jsx(_components.code, {
            children: "cpm"
          }), " and ", _jsx(_components.code, {
            children: "revshare"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Charge for use of the segment"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Segment owner"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Description for the segment"
          }), " (optional)"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences",
        children: "Syncing audiences"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Magnite Data audiences."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync audience fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default audience fields.\nMagnite requires the following fields, so you must map them to complete your configuration:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "User ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "User ID type"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Update type"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure your model's columns have the same data types as the fields you want to sync to."
      }), "\n", _jsx(_components.h2, {
        id: "magnite-match-integration",
        children: "Magnite Match integration"
      }), "\n", _jsx(_components.h3, {
        id: "sync-configuration-1",
        children: "Sync configuration"
      }), "\n", _jsx(_components.p, {
        children: "Select the relevant model and the Magnite Match destination you want to sync to. Then, enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Segment name"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Description for the segment"
          }), " (optional)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "CPM for the segment"
          }), " (optional)"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Platform for the segment"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Membership timestamp column"
          }), " (optional)"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Full resync interval (in days)"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences-1",
        children: "Syncing audiences"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Magnite Match audiences."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync audience fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default audience fields.\nMagnite requires the following fields, so you must map them to complete your configuration:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email (SHA256)"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure your model's columns have the same data types as the fields you want to sync to."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
